import URI from 'urijs';
import HmacSHA1 from 'crypto-js/hmac-sha1';
import Base64 from 'crypto-js/enc-base64';
import entries from 'lodash/entries';
import fromPairs from 'lodash/fromPairs';
import remove from 'lodash/remove';

const CryptoJS = { HmacSHA1, enc: { Base64 } };

function oauth() {
  function guidPart() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }

  function excludeRealm(param) {
    return param[0] !== 'realm';
  }

  function compareBaseStringParams(a, b) {
    return a[0] !== b[0] ? (a[0] > b[0] ? 1 : -1) : a[1] > b[1] ? 1 : -1;
  }

  function convertEncodedStringToParams(str) {
    const requestParams = URI.parseQuery(str, true);
    const params = [];

    function pushIt(key, val) {
      if (Array.isArray(val)) {
        val.forEach(pushIt.bind(null, key));
      } else {
        params.push([key, val]);
      }
    }

    Object.keys(requestParams).forEach(function (key) {
      pushIt(key, requestParams[key]);
    });

    return params;
  }

  function concatParamString(paramString, param) {
    return (
      (paramString ? paramString + '&' : '') +
      URI.buildQueryParameter(param[0], param[1], false)
    );
  }

  this.generateNonce = function () {
    return (
      guidPart() +
      guidPart() +
      guidPart() +
      guidPart() +
      guidPart() +
      guidPart() +
      guidPart() +
      guidPart()
    );
  };

  this.buildOAuthParams = function (clientKey, nonce, options) {
    options = options || {};

    const params = [
      ...entries(options.params),
      ['realm', ''],
      ['oauth_consumer_key', clientKey],
      ['oauth_signature_method', 'HMAC-SHA1'],
      ['oauth_timestamp', Math.floor(Date.now() / 1000)],
      ['oauth_nonce', nonce],
      ['oauth_version', '1.0'],
    ];

    if (Object.prototype.hasOwnProperty.call(options, 'resourceOwner')) {
      params.push(['oauth_token', options.resourceOwner.oauth_token]);
    }
    if (Object.prototype.hasOwnProperty.call(options, 'callbackUri')) {
      params.push(['oauth_callback', options.callbackUri]);
    }
    if (Object.prototype.hasOwnProperty.call(options, 'verifier')) {
      params.push(['oauth_verifier', options.verifier]);
    }
    // if(options.hasOwnProperty('rpp')) {
    //   params.push(['rpp', options.rpp]);
    // }
    return params;
  };

  this.buildBaseString = function (method, url, body, oauthParams) {
    const parsedUrl = new URI(url);
    const params = oauthParams.filter(excludeRealm);

    params.push.apply(params, convertEncodedStringToParams(parsedUrl.query()));
    params.push.apply(params, convertEncodedStringToParams(body));
    params.sort(compareBaseStringParams);

    return [
      method.toUpperCase(),
      URI.encode(
        (parsedUrl.protocol() + '://' + parsedUrl.host()).toLowerCase() +
          parsedUrl.path(),
      ),
      URI.encode(params.reduce(concatParamString, '')),
    ].join('&');
  };

  this.signBaseString = function (consumerSecret, tokenSecret, base) {
    const key = consumerSecret + '&' + (tokenSecret || '');
    return CryptoJS.HmacSHA1(base, key).toString(CryptoJS.enc.Base64);
  };

  this.appendSignatureToParams = function (signature, oauthParams) {
    oauthParams.push(['oauth_signature', signature]);
  };

  this.buildSignedOAuthParams = function (method, uri, body, options) {
    options = options || {};
    let oauthParams;
    const client = options.client || {};
    const resourceOwner = options.resourceOwner || {};

    this.appendSignatureToParams(
      this.signBaseString(
        client.oauth_token_secret,
        resourceOwner.oauth_token_secret,
        this.buildBaseString(
          method,
          String(uri),
          body,
          (oauthParams = this.buildOAuthParams(
            client.oauth_token,
            this.generateNonce(),
            options,
          )),
        ),
      ),
      oauthParams,
    );

    return oauthParams;
  };

  this.buildOAuthHeader = function (method, uri, body, options) {
    const oauthParams = this.buildSignedOAuthParams(method, uri, body, options);

    return (
      'OAuth ' +
      oauthParams
        .map(function (element) {
          return element[0] + '="' + element[1] + '"';
        })
        .join(',')
    );
  };

  this.buildOAuthUri = function (method, uri, body, options) {
    let oauthParams = this.buildSignedOAuthParams(method, uri, body, options);
    const parsedUri = new URI(uri);

    oauthParams = remove(oauthParams, function (keyPair) {
      return !!keyPair[1];
    });

    parsedUri.query(fromPairs(oauthParams));

    return parsedUri.toString();
  };

  this.parseJwt = function (token) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );

    return JSON.parse(jsonPayload);
  };
}

export default new oauth();
